import { ReactNode, createContext, useCallback, useContext, useState } from "react"

import { UrlQueryParamContext } from "@oysterhr/quarantine"
import { ReusableWizardStep } from "./ReusableWizardStep"

export type ReusableWizardContextProps = {
  currentPageNumber: number
  totalPages: number
  isFirstPage: boolean
  isLastPage: boolean
  goToNext: () => void
  goToPrevious: () => void
  isNextDisabled: boolean
  setIsNextDisabled: (isDisabled: boolean) => void
  isBackDisabled: boolean
  setIsBackDisabled: (isDisabled: boolean) => void
}

const ReusableWizardContext = createContext<ReusableWizardContextProps | null>(null)

type ReusableWizardProps = {
  children: ReactNode[]
  scrollTopAfterTransition?: boolean
  onTransition?: () => void
  unstyled?: boolean
  fallback?: React.ReactNode
}

export function ReusableWizard({
  children,
  scrollTopAfterTransition = false,
  onTransition,
  unstyled = false,
  fallback = null,
}: ReusableWizardProps) {
  const { params, setParam } = useContext(UrlQueryParamContext)

  // .filter(Boolean) will strip false, null, and undefined from conditional rendering
  // .flat() will allow us to mix static and dynamically mapped children in a single array
  // [Step1, [StepDynamic1, StepDynamic2], Step 3] -> [Step1, StepDynamic1, StepDynamic2, Step 3]
  children = children.filter(Boolean).flat()
  const totalPages = children.length

  const currentPageNumber = parseInt((params?.page ?? "1") as string)
  const isLastPage = currentPageNumber >= totalPages
  const isFirstPage = currentPageNumber === 1
  const currentPage = children[currentPageNumber - 1]

  const onBack = useCallback(() => {
    const previousPageIndex = currentPageNumber - 1
    onTransition?.()
    setParam("page", previousPageIndex === 1 ? "" : String(previousPageIndex))
  }, [currentPageNumber, setParam])

  const onNext = useCallback(async () => {
    const nextPageIndex = currentPageNumber + 1
    setParam("page", String(nextPageIndex))
    onTransition?.()
    if (scrollTopAfterTransition) {
      requestAnimationFrame(() => {
        window.scrollTo(0, 0)
      })
    }
  }, [currentPageNumber, setParam, scrollTopAfterTransition])

  const [isNextDisabled, setIsNextDisabled] = useState(false)
  const [isBackDisabled, setIsBackDisabled] = useState(false)

  return (
    <ReusableWizardContext.Provider
      value={{
        currentPageNumber,
        totalPages,
        isFirstPage,
        isLastPage,
        isNextDisabled,
        setIsNextDisabled,
        isBackDisabled,
        setIsBackDisabled,
        goToPrevious: onBack,
        goToNext: onNext,
      }}
    >
      {unstyled ? (
        currentPage
      ) : (
        <ReusableWizardStepWrapper>{currentPage}</ReusableWizardStepWrapper>
      )}

      {!currentPage && fallback}
    </ReusableWizardContext.Provider>
  )
}

export const ReusableWizardStepWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="relative w-136 rounded-lg border-2 border-gray-200 bg-white p-2 md:p-8">
      {children}
    </div>
  )
}

export function useReusableWizard() {
  const ctx = useContext(ReusableWizardContext)

  if (!ctx) {
    throw new Error(
      "useReusableWizard can only be used inside a ReusableWizardContext Provider. Make sure to wrap your component in a <ReusableWizard />",
    )
  }
  return ctx
}

/**
 * We're making this component a static property of the ReusableWizard component because it doesn't make sense to render
 * this component if the wizard is not being used.
 */
ReusableWizard.Step = ReusableWizardStep
